import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { AuthService } from '@services/auth.service';
import { NotificationService } from '@services/notification.service';
import { AuthenticatedUser } from '@shared/types/authenticated-user';
import { MfaSetupResponse } from '@shared/types/mfa';
import { take } from 'rxjs';

@Component({
  selector: 'app-mfa-setup',
  templateUrl: './mfa-setup.component.html',
  styleUrl: './mfa-setup.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MfaSetupComponent {
  private formBuilder = inject(FormBuilder);
  private authService = inject(AuthService);
  private dialogRef = inject<MatDialogRef<MfaSetupComponent>>(MatDialogRef);
  private notificationService = inject(NotificationService);
  private dialogData = inject<{ preAuthToken: string }>(MAT_DIALOG_DATA);
  private cdr = inject(ChangeDetectorRef);

  isLoading$ = this.authService.isLoading$;
  setupData: MfaSetupResponse | null = null;
  loggedInUser: AuthenticatedUser | null = null;

  confirmForm = this.formBuilder.group({
    code: [
      '',
      [Validators.required, Validators.minLength(6), Validators.maxLength(6)],
    ],
  });

  constructor() {
    this.authService
      .setupMfaWithToken(this.dialogData.preAuthToken)
      .pipe(take(1))
      .subscribe({
        next: (data) => {
          this.setupData = data;
          this.cdr.detectChanges();
        },
        error: (err: Error) => {
          this.notificationService.showNotification(err.message);
          this.dialogRef.close();
        },
      });
  }

  confirmCode(): void {
    if (this.confirmForm.invalid) return;
    this.authService
      .confirmMfaSetup(
        this.dialogData.preAuthToken,
        this.confirmForm.get('code')!.value!,
      )
      .pipe(take(1))
      .subscribe({
        next: (user) => {
          this.loggedInUser = user;
          this.cdr.detectChanges();
        },
        error: () => {
          this.notificationService.showNotification('mfa.invalidCode');
        },
      });
  }

  close(): void {
    this.dialogRef.close(this.loggedInUser ?? undefined);
  }
}
